/**
 * Formula registry — pure functions that correspond to Log Pro's
 * inline parameter-adjustment lambdas, keyed by a stable string ID.
 *
 * Every function signature: `(diff: number, volumeGal: number) => number`
 * where `diff` = delta from current to target in the formula's base unit
 * and `volumeGal` = tank volume in US gallons. The return value is the
 * dose amount expressed in the formula's `output_unit`.
 */
/** Typed error thrown when a formula ID is not registered. */
export class UnknownFormulaError extends Error {
    constructor(id) {
        super(`Unknown formula: "${id}"`);
        this.name = 'UnknownFormulaError';
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// Registry
// ═══════════════════════════════════════════════════════════════════════════
export const formulas = {
    // Brightwell Aquatics
    'brightwell-reef-code-a': (diff, vol) => (1 / 16) * diff * vol,
    'brightwell-reef-code-b': (diff, vol) => (1 / 0.8) * (diff / 2.8) * vol,
    'brightwell-magnesion-liquid': (diff, vol) => diff * vol / 26,
    'brightwell-magnesion-powder': (diff, vol) => diff * vol / 114.4,
    // BRS
    'brs-soda-ash-liquid': (diff, vol) => (1 / 0.5) * diff * vol,
    'brs-bicarbonate-liquid': (diff, vol) => (1 / 0.25) * diff * vol,
    'brs-soda-ash-dry': (diff, vol) => diff * 0.200617872 * vol,
    'brs-bicarbonate-dry': (diff, vol) => diff * 0.318028256 * vol,
    'brs-calcium-liquid': (diff, vol) => (1 / 9.77) * diff * vol,
    'brs-calcium-dry': (diff, vol) => (1 / 72) * diff * vol,
    'brs-magnesium-liquid': (diff, vol) => diff * vol / 12.4,
    'brs-magnesium-chloride-dry': (diff, vol) => diff * vol / 31.6875,
    'brs-magnesium-sulfate-dry': (diff, vol) => diff * vol / 54.85,
    // E.S.V.
    'esv-bionic-part-b': (diff, vol) => (1 / 0.8) * diff * vol,
    'esv-bionic-part-a-est': (diff, vol) => (1 / 16) * (diff * 20) * vol,
    'esv-magnesium': (diff, vol) => diff * vol / 10,
    // General
    'kalkwasser-solution': (diff, vol) => (1 / 0.2536) * diff * vol,
    // Seachem
    'seachem-cupramine': (diff, vol) => vol * diff / 2.625,
    'seachem-discus-buffer': (diff, vol) => vol * diff * 1.75,
    'seachem-equilibrium': (diff, vol) => vol * diff * 0.8,
    'seachem-flourish-iron': (diff, vol) => vol * diff * 1,
    'seachem-flourish-nitrogen': (diff, vol) => vol * diff / 5.3,
    'seachem-flourish-phosphorus': (diff, vol) => vol * diff / 2.7,
    'seachem-flourish-potassium': (diff, vol) => vol * diff / 125,
    'seachem-marine-buffer': (diff, vol) => vol * diff * 1.5,
    'seachem-neutral-regulator': (diff, vol) => vol * Math.abs(7.0 - diff) * 1.75,
    'seachem-reef-advantage-calcium': (diff, vol) => vol * diff * 0.0104167,
    'seachem-reef-advantage-mag': (diff, vol) => vol * diff / 8,
    'seachem-reef-advantage-strontium': (diff, vol) => vol * diff * 0.02,
    'seachem-reef-builder': (diff, vol) => vol * diff * 0.3028328,
    'seachem-reef-carbonate': (diff, vol) => vol * diff * 0.9463525,
    'seachem-reef-fusion': (diff, vol) => vol * diff * 0.0378541,
    // Two Little Fishies
    'tlf-cbalance-part-b': (diff, vol) => (1 / 0.5) * diff * vol,
    'tlf-cbalance-part-a-est': (diff, vol) => (1 / 10) * (diff * 20) * vol,
};
/**
 * Resolve a formula by ID. Throws `UnknownFormulaError` with a helpful
 * message when the ID is not registered.
 */
export function getFormula(id) {
    const fn = formulas[id];
    if (!fn) {
        throw new UnknownFormulaError(id);
    }
    return fn;
}
//# sourceMappingURL=formulas.js.map